import {
	parseWorkspaceItems,
	parseWorkspaceSearchHits,
} from "./schemas/workspace-context-schema";
import type { AgentChatPersonMention } from "./types";

export type SuggestPeopleFn = (params: {
	query: string;
	limit?: number;
}) => Promise<
	Array<{
		id: string;
		displayName: string;
		email: string;
		avatarUrl?: string | null;
	}>
>;

export type ResolvePeopleMentionContextParams = {
	text: string;
	mentions?: AgentChatPersonMention[];
	agentTools?: Record<string, unknown>;
	suggestPeopleFn?: SuggestPeopleFn;
};

const MENTION_REGEX =
	/(?:^|\s)(?:@\[([^\]]+)\]|@"([^"]+)"|@([a-zA-Z0-9._-]+))/g;

async function executeTool(
	tool: unknown,
	args: Record<string, unknown>,
): Promise<unknown> {
	if (
		tool &&
		typeof tool === "object" &&
		"execute" in tool &&
		typeof (tool as { execute: unknown }).execute === "function"
	) {
		const executor = tool as {
			execute: (args: unknown, options?: unknown) => Promise<unknown>;
		};
		return await executor.execute(args, {
			toolCallId: `mention_ctx_${Date.now()}`,
			messages: [],
		});
	}
	return null;
}

function extractMentionNamesFromText(text: string): string[] {
	const names: string[] = [];
	const matches = text.matchAll(MENTION_REGEX);
	for (const match of matches) {
		const candidate = (match[1] ?? match[2] ?? match[3])?.trim();
		if (candidate && !names.includes(candidate)) {
			names.push(candidate);
		}
	}
	return names;
}

function formatPersonXml(item: {
	id: string;
	name: string;
	email?: string | null;
	team?: string | null;
	markdown?: string;
}): string {
	const attrs = [
		`id="${escapeXml(item.id)}"`,
		`name="${escapeXml(item.name)}"`,
	];
	if (item.email) {
		attrs.push(`email="${escapeXml(item.email)}"`);
	}
	if (item.team) {
		attrs.push(`team="${escapeXml(item.team)}"`);
	}

	const body =
		item.markdown ||
		`# ${item.name}${item.email ? `\n- **Email:** ${item.email}` : ""}`;
	return `<person ${attrs.join(" ")}>\n${body}\n</person>`;
}

function escapeXml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&apos;");
}

export async function resolvePeopleMentionContext({
	text,
	mentions = [],
	agentTools,
	suggestPeopleFn,
}: ResolvePeopleMentionContextParams): Promise<string | null> {
	const getTool = agentTools?.["workspace.get"] ?? agentTools?.workspace_get;
	const searchTool =
		agentTools?.["workspace.search"] ?? agentTools?.workspace_search;

	const personEnvelopes: string[] = [];
	const seenIds = new Set<string>();

	const directIds = mentions
		.map((m) => m.id)
		.filter((id) => id.startsWith("User-"));

	if (directIds.length > 0 && getTool) {
		try {
			const rawGet = await executeTool(getTool, { ids: directIds });
			const items = parseWorkspaceItems(rawGet);
			for (const item of items) {
				if (!seenIds.has(item.id)) {
					seenIds.add(item.id);
					personEnvelopes.push(
						item.llmContent ||
							formatPersonXml({
								id: item.id,
								name: item.name,
								markdown: item.markdown,
							}),
					);
				}
			}
		} catch (err) {
			console.warn("[resolvePeopleMentionContext] workspace.get error:", err);
		}
	}

	for (const mention of mentions) {
		if (!seenIds.has(mention.id)) {
			seenIds.add(mention.id);
			personEnvelopes.push(
				formatPersonXml({
					id: mention.id,
					name: mention.displayName,
					email: mention.email,
				}),
			);
		}
	}

	const textMentionNames = extractMentionNamesFromText(text).filter(
		(name) =>
			!mentions.some((m) => m.displayName.toLowerCase() === name.toLowerCase()),
	);

	for (const name of textMentionNames) {
		let resolved = false;
		if (searchTool && getTool) {
			try {
				const rawSearch = await executeTool(searchTool, {
					query: name,
					kinds: ["PERSON"],
					limit: 5,
				});
				const hits = parseWorkspaceSearchHits(rawSearch).filter(
					(h) => h.kind.toUpperCase() === "PERSON",
				);
				const hitIds = hits.map((h) => h.id).filter((id) => !seenIds.has(id));

				if (hitIds.length > 0) {
					const rawGet = await executeTool(getTool, { ids: hitIds });
					const items = parseWorkspaceItems(rawGet);
					for (const item of items) {
						if (!seenIds.has(item.id)) {
							seenIds.add(item.id);
							personEnvelopes.push(
								item.llmContent ||
									formatPersonXml({
										id: item.id,
										name: item.name,
										markdown: item.markdown,
									}),
							);
							resolved = true;
						}
					}
				}
			} catch (err) {
				console.warn(
					"[resolvePeopleMentionContext] workspace.search error:",
					err,
				);
			}
		}

		if (!resolved && suggestPeopleFn) {
			try {
				const suggestions = await suggestPeopleFn({ query: name, limit: 3 });
				const match = suggestions.find(
					(s) =>
						s.displayName.toLowerCase().includes(name.toLowerCase()) ||
						s.email.toLowerCase().includes(name.toLowerCase()),
				);
				if (match && !seenIds.has(match.id)) {
					seenIds.add(match.id);
					personEnvelopes.push(
						formatPersonXml({
							id: match.id,
							name: match.displayName,
							email: match.email,
						}),
					);
				}
			} catch (err) {
				console.warn(
					"[resolvePeopleMentionContext] suggestPeople fallback error:",
					err,
				);
			}
		}
	}

	if (personEnvelopes.length === 0) {
		return null;
	}

	return `<people>\n${personEnvelopes.join("\n\n")}\n</people>`;
}
